import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { nextPosition } from '../videoTrim';
import type { Shot } from '../types';
import type { Layer } from '../useFilmEngine';
import { AnimationRegistry, enterAnimation, exitAnimation, motionAnimation } from '../transitions';
import { DepthPhoto } from './DepthPhoto';

/** Photos within this aspect mismatch fill the frame; beyond it, letterbox over a blur. */
const COVER_TOLERANCE = 1.22;

interface Props {
  layer: Layer;
  shot: Shot;
  /** The layer the viewer is watching (not the one leaving). */
  active: boolean;
  playing: boolean;
  viewportAspect: number;
  videoVolume: number;
  registry: AnimationRegistry;
  onExited: (key: string) => void;
  onVideoEnded: (shotIndex: number) => void;
  onShotError: (shotIndex: number) => void;
  registerVideo: (shotIndex: number, el: HTMLVideoElement | null) => void;
  /** The browser refused to start video sound before a user gesture. */
  onSoundBlocked: () => void;
}

/**
 * One memory on the stage.
 *
 *   outer element  -> entrance / exit transition (opacity, push, zoom, blur)
 *   inner element  -> Ken Burns move (photos only)
 *
 * Keyed by layer, never by settings, so changing pace or volume mid-shot
 * never remounts it — a playing video keeps its position.
 */
function ShotLayerImpl({
  layer,
  shot,
  active,
  playing,
  viewportAspect,
  videoVolume,
  registry,
  onExited,
  onVideoEnded,
  onShotError,
  registerVideo,
  onSoundBlocked,
}: Props) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const [flat, setFlat] = useState(false);
  const [aspect, setAspect] = useState<number | null>(
    shot.item.width && shot.item.height ? shot.item.width / shot.item.height : null,
  );
  const { item } = shot;

  // Entrance + Ken Burns start before first paint, so there is no flash of
  // the final state.
  useLayoutEffect(() => {
    const el = outer.current;
    if (!el) return;
    const enter = enterAnimation(layer.enter.kind, layer.enter.ms);
    if (enter) registry.add(el.animate(enter.keyframes, enter.options));
    if (shot.motion && inner.current) {
      const m = motionAnimation(shot.motion.from, shot.motion.to, shot.motion.durationMs);
      registry.add(inner.current.animate(m.keyframes, m.options));
    }
    // Entrance is decided once per layer; later prop changes must not replay it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exit: animate out, then ask to be removed.
  useEffect(() => {
    if (!layer.leave) return;
    const el = outer.current;
    const exit = el ? exitAnimation(layer.leave.kind, layer.leave.ms) : null;
    if (!el || !exit) {
      onExited(layer.key);
      return;
    }
    const anim = registry.add(el.animate(exit.keyframes, exit.options));
    let done = false;
    anim.finished
      .then(() => {
        done = true;
        onExited(layer.key);
      })
      .catch(() => undefined);
    return () => {
      if (!done) anim.cancel();
    };
  }, [layer.leave, layer.key, onExited, registry]);

  // ---- Video control -----------------------------------------------------------
  useEffect(() => {
    if (shot.kind !== 'video') return;
    registerVideo(shot.index, videoRef.current);
    return () => registerVideo(shot.index, null);
  }, [registerVideo, shot.index, shot.kind]);

  // Volume changes glide over ~300 ms (e.g. dipping under the narration), never jump.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (videoVolume > 0) v.muted = false;
    const from = v.volume;
    const steps = 10;
    let n = 0;
    const t = window.setInterval(() => {
      n++;
      v.volume = Math.min(1, Math.max(0, from + ((videoVolume - from) * n) / steps));
      if (n >= steps) {
        window.clearInterval(t);
        v.muted = videoVolume === 0;
      }
    }, 30);
    return () => window.clearInterval(t);
  }, [videoVolume]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || failed) return;
    if (active && playing) {
      v.play().catch((err: Error) => {
        // Autoplay with sound was refused: keep the picture moving silently
        // and offer a tap to turn sound on.
        if (err.name === 'NotAllowedError' && !v.muted) {
          v.muted = true;
          onSoundBlocked();
          v.play().catch(() => undefined);
        }
      });
    } else {
      v.pause();
    }
  }, [active, playing, failed, onSoundBlocked]);

  // Smart trim: play only the kept parts, jumping over dead stretches; at the
  // end of the last part, stop and tell the film (like the clip's own end).
  const trim = shot.kind === 'video' ? shot.item.trim : null;
  const trimDone = useRef(false);
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !trim || !active || !playing || failed) return;
    const t = window.setInterval(() => {
      if (trimDone.current || v.readyState < HTMLMediaElement.HAVE_METADATA) return;
      const to = nextPosition(trim, v.currentTime);
      if (to === null) {
        trimDone.current = true;
        v.pause();
        onVideoEnded(shot.index);
      } else if (to !== v.currentTime) {
        v.currentTime = to;
      }
    }, 40);
    return () => window.clearInterval(t);
  }, [trim, active, playing, failed, onVideoEnded, shot.index]);

  // Release the decoder when the layer really goes away. Effect cleanups also
  // run while the element stays mounted (React dev double-invocation, Fast
  // Refresh); emptying a still-mounted <video> would leave it with no source
  // that React never restores. So release only once it has left the page.
  useEffect(() => {
    const v = videoRef.current;
    return () => {
      if (!v) return;
      window.setTimeout(() => {
        if (v.isConnected) return;
        v.pause();
        v.removeAttribute('src');
        v.load();
      }, 0);
    };
  }, []);

  // Watchdog: a clip that never receives data must not hang the film.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !active || !playing || failed) return;
    const t = window.setTimeout(() => {
      if (v.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) fail();
    }, 12000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, playing, failed]);

  const fail = () => {
    setFailed(true);
    onShotError(shot.index);
  };

  const mismatch = aspect ? Math.max(aspect / viewportAspect, viewportAspect / aspect) : 1;
  const photoCover = shot.kind === 'photo' && mismatch <= COVER_TOLERANCE;
  const needsFill = shot.kind === 'video' ? mismatch > 1.04 : !photoCover;
  const fillSrc = item.thumbnailUrl ?? item.posterUrl ?? null;
  // Video posters are padded to 16:9 with black, so a blurred poster of a
  // PORTRAIT clip is mostly black. Frame those with a soft charcoal glow
  // instead; landscape clips and photos get the blurred fill.
  const portraitVideo = shot.kind === 'video' && aspect !== null && aspect < 1;

  return (
    <div ref={outer} className="absolute inset-0 overflow-hidden bg-[#0b0907]" style={{ willChange: 'opacity, transform' }}>
      {needsFill && portraitVideo && (
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 60% 75% at center, #2a241c 0%, #14110d 55%, #0b0907 100%)' }}
        />
      )}
      {needsFill && !portraitVideo && fillSrc && (
        <img
          src={fillSrc}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-125 object-cover opacity-55 blur-3xl"
        />
      )}

      {failed ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
          <p className="text-sm font-medium text-[#f3ece2]/85">This memory couldn’t be shown</p>
          <p className="text-xs text-[#f3ece2]/45">
            {shot.kind === 'video' ? 'The video file is missing or in a format this browser can’t play.' : 'The photo file is missing or damaged.'}
            {' '}Moving on…
          </p>
        </div>
      ) : shot.kind === 'photo' ? (
        <div ref={inner} className="absolute inset-0" style={{ willChange: 'transform' }}>
          {shot.parallax && item.depthUrl && !flat ? (
            <DepthPhoto
              src={item.displayUrl ?? item.url}
              depthSrc={item.depthUrl}
              plan={shot.parallax}
              fit={photoCover ? 'cover' : 'contain'}
              registry={registry}
              onFallback={() => setFlat(true)}
              onAspect={setAspect}
              label={item.originalFilename}
            />
          ) : (
            <img
              src={item.displayUrl ?? item.url}
              alt={item.originalFilename}
              draggable={false}
              onLoad={(e) => {
                const img = e.currentTarget;
                if (img.naturalWidth && img.naturalHeight) setAspect(img.naturalWidth / img.naturalHeight);
              }}
              onError={fail}
              className={`h-full w-full ${photoCover ? 'object-cover' : 'object-contain drop-shadow-[0_20px_60px_rgba(0,0,0,0.6)]'}`}
            />
          )}
        </div>
      ) : (
        <video
          ref={videoRef}
          data-film-video=""
          src={item.url}
          poster={item.posterUrl ?? undefined}
          playsInline
          preload="auto"
          className="absolute inset-0 h-full w-full object-contain"
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            // Start at the first part worth watching.
            if (trim && v.currentTime < trim.segments[0][0]) v.currentTime = trim.segments[0][0];
            if (v.videoWidth && v.videoHeight) setAspect(v.videoWidth / v.videoHeight);
          }}
          onEnded={() => {
            if (trimDone.current) return;
            trimDone.current = true;
            onVideoEnded(shot.index);
          }}
          onError={fail}
        />
      )}
    </div>
  );
}

export const ShotLayer = memo(ShotLayerImpl);
